import React, { useEffect, useRef } from 'react';

import { ConfiguratorGroupDto } from '@/services/configurator';
import { useActiveGroup } from './useActiveGroup';
import { GroupList } from './GroupList';
import { GroupOptionDetail } from './GroupOptionDetail';
import { StyleSelectionGate } from '../../RoomStyle/StyleSelectionGate';

interface RoomOptionTwoTierListProps {
    groups: ConfiguratorGroupDto[];
    is3d?: boolean;
    /** Fired when a group is opened — used to pre-load that group's configurator visuals. */
    onGroupExpand?: (group: ConfiguratorGroupDto) => void;
    /** Fired when the open group changes (null when none). Lets a parent overlay react to it. */
    onActiveGroupChange?: (group: ConfiguratorGroupDto | null) => void;
}

/**
 * The showroom options menu. Tier 1 is a flat list of option groups; clicking one navigates into
 * Tier 2 — that group's option-selection view (in place, single panel). For the centered card-grid
 * layout used when there's no room visualization, see `RoomOptionCards`. The authenticated
 * buyer/seller configurator uses the accordion instead (see `RoomOptionMenu`).
 */
export const RoomOptionTwoTierList: React.FC<RoomOptionTwoTierListProps> = ({
    groups,
    is3d = false,
    onGroupExpand,
    onActiveGroupChange,
}) => {
    const { activeGroup, activeGroupId, openGroup, closeGroup, swallowGhostClick } = useActiveGroup(
        groups,
        onGroupExpand
    );

    // Preserve the Tier-1 list's scroll position across the switch into an option group's items (Tier
    // 2) and back. GroupList owns its own scroll container (`.listScroll`) and unmounts when a group
    // opens, so the position is stashed here — this component stays mounted — and restored on return.
    const listScrollTopRef = useRef(0);

    // Surface the open group to a parent (e.g. so the visualized configurator can overlay the visual
    // panel for groups that aren't mapped to the 3D visualization). Key on the id so a data refetch
    // (new group object, same id) doesn't fire spurious changes.
    useEffect(() => {
        onActiveGroupChange?.(activeGroup);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeGroupId]);

    const content = activeGroup ? (
        <GroupOptionDetail group={activeGroup} is3d={is3d} onClose={closeGroup} />
    ) : (
        // Closing the detail puts the group rows back where the Close button just was — swallow the
        // ghost click that follows the closing tap so it can't re-open a group.
        <GroupList
            groups={groups}
            is3d={is3d}
            onOpenGroup={openGroup}
            scrollTopRef={listScrollTopRef}
            onClickCapture={swallowGhostClick}
        />
    );

    // Gate the whole menu behind style selection: a room with unpicked required styles shows a
    // "choose your style" prompt instead of the option groups until the user resolves it.
    return <StyleSelectionGate>{content}</StyleSelectionGate>;
};
